import React, { useState } from 'react';
import { Activity, TrendingUp, AlertTriangle, CheckCircle, Lightbulb, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import api from '../../../services/api';

export default function GlucoseSimulator({ meal, onClose }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);

  const unit = localStorage.getItem('diabetes_unit') || 'mmol/l';

  const runSimulation = async () => {
    setLoading(true);
    try {
      const data = await api.post('/glucose-sim/predict', {
        meal_name: meal.name || 'Meal',
        calories: meal.calories || 0,
        carbs: meal.carbs || 0,
        protein: meal.protein || 0,
        fat: meal.fat || 0,
        fiber: meal.fiber || 0,
        mealtime: meal.mealTime || 'lunch',
        unit,
        insulin_dose: meal.insulinDose || null,
      });
      if (data.prediction) {
        setPrediction(data);
      } else {
        toast.error(data.detail || 'Failed to get prediction');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to simulate');
    } finally {
      setLoading(false);
    }
  };

  const riskColors = {
    low: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', icon: CheckCircle },
    moderate: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: AlertTriangle },
    high: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle },
  };

  if (!prediction) {
    return (
      <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4" data-testid="glucose-simulator">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="h-4 w-4 text-purple-400" />
          <span className="text-sm font-medium text-white">Glucose Impact Simulator</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Predict how <span className="text-white font-medium">{meal.name}</span> ({Math.round(meal.carbs || 0)}g carbs, {Math.round(meal.calories || 0)} kcal)
          will affect your blood sugar based on your personal history.
        </p>
        <div className="flex gap-2">
          <Button
            onClick={runSimulation}
            disabled={loading}
            className="flex-1 bg-purple-600 hover:bg-purple-500 text-sm"
            data-testid="run-glucose-sim"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Analyzing...</>
            ) : (
              <><Activity className="h-4 w-4 mr-1" /> Predict Impact</>
            )}
          </Button>
          {onClose && (
            <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-400 text-sm">
              Skip
            </Button>
          )}
        </div>
      </div>
    );
  }

  const pred = prediction.prediction;
  const risk = riskColors[pred.risk_level] || riskColors.moderate;
  const RiskIcon = risk.icon;
  const curve = pred.curve || [];

  // Find max value for chart scaling
  const maxVal = Math.max(...curve.map(c => c.value), 1);
  const minVal = Math.min(...curve.map(c => c.value), 0);
  const range = maxVal - minVal || 1;

  // Threshold line
  const highThreshold = unit === 'mg/dl' ? 162 : 9.0;

  return (
    <div className={`${risk.bg} border ${risk.border} rounded-xl p-4`} data-testid="glucose-prediction-result">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-purple-400" />
          <span className="text-sm font-medium text-white">Predicted Glucose Response</span>
        </div>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${risk.bg} ${risk.border} border`}>
          <RiskIcon className={`h-3 w-3 ${risk.text}`} />
          <span className={`text-[10px] font-medium ${risk.text} capitalize`}>{pred.risk_level} Risk</span>
        </div>
      </div>

      {/* Glucose Curve Chart */}
      {curve.length > 0 && (
        <div className="bg-slate-900/60 rounded-lg p-3 mb-3">
          <div className="flex items-end justify-between h-24 gap-1 relative">
            {/* High threshold line */}
            <div
              className="absolute left-0 right-0 border-t border-dashed border-red-500/30"
              style={{ bottom: `${((highThreshold - minVal) / range) * 100}%` }}
            >
              <span className="absolute -top-3 right-0 text-[8px] text-red-400">{highThreshold}</span>
            </div>
            {curve.map((point, i) => {
              const height = ((point.value - minVal) / range) * 100;
              const isAboveThreshold = point.value > highThreshold;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className={`text-[9px] font-mono ${isAboveThreshold ? 'text-red-400' : 'text-green-400'}`}>
                    {point.value}
                  </span>
                  <div className="w-full flex items-end justify-center" style={{ height: '60px' }}>
                    <div
                      className={`w-full max-w-[20px] rounded-t transition-all ${
                        isAboveThreshold ? 'bg-red-500/60' : 'bg-green-500/60'
                      }`}
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                  </div>
                  <span className="text-[8px] text-slate-500">{point.time}m</span>
                </div>
              );
            })}
          </div>
          {/* Peak info */}
          <div className="flex justify-between mt-2 pt-2 border-t border-slate-700/50">
            <span className="text-[10px] text-slate-500">
              Peak: <span className={`font-medium ${pred.predicted_peak > highThreshold ? 'text-red-400' : 'text-green-400'}`}>
                {pred.predicted_peak} {unit}
              </span> at {pred.peak_time_minutes}min
            </span>
            <span className="text-[10px] text-slate-500">
              Meal: {Math.round(meal.carbs || 0)}g carbs
            </span>
          </div>
        </div>
      )}

      {/* Explanation */}
      {pred.explanation && (
        <p className="text-xs text-slate-300 mb-3 leading-relaxed">{pred.explanation}</p>
      )}

      {/* Tips */}
      {pred.tips && pred.tips.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {pred.tips.map((tip, i) => (
            <div key={i} className="flex items-start gap-2 bg-slate-900/40 rounded-lg p-2">
              <Lightbulb className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
              <span className="text-[11px] text-slate-300 leading-relaxed">{tip}</span>
            </div>
          ))}
        </div>
      )}

      {/* Close */}
      {onClose && (
        <Button variant="outline" size="sm" onClick={onClose} className="w-full border-slate-600 text-slate-400 text-xs mt-1">
          Close
        </Button>
      )}
    </div>
  );
}
